import axios from 'axios';
import { apiUrl } from '../utils/constants.ts'; // Assuming apiUrl is defined here
// --- REMOVE: Unused auth import ---
// import { getAuthToken } from './authService'; // Assuming you have this
// --- END REMOVE ---

// Define the structure of a message object (matching backend)
export interface Message {
    _id?: string; // Optional: ID assigned by the database
    senderAddress: string;
    receiverAddress: string;
    messageType: 'text' | 'image'; // Added message type
    content?: string; // Optional: Content for text messages
    imageUrl?: string; // Optional: URL for image messages
    replyToMessageId?: string | null; // Optional: ID of the message being replied to
    replyToMessage?: Partial<Message> | null; // Optional: Populated details of the replied message (added by backend)
    timestamp: string; // ISO 8601 date string
    read: boolean;
}

// --- NEW: Define AppNotification type (matching frontend) ---
export interface AppNotification {
    id: string | number;
    type: 'message' | 'invite' | string;
    text: string;
    senderAddress?: string;
    data?: any;
    read: boolean;
    timestamp: number;
}
// --- END NEW ---

// --- NEW: Function to upload an image ---
/**
 * Uploads an image file for chat.
 * @param imageFile - The image file object to upload.
 * @param userAddress - The address of the user uploading the image (for authentication).
 * @returns Promise<{success: boolean, imageUrl?: string, error?: string}>
 */
export const uploadImage = async (imageFile: File, userAddress: string): Promise<{success: boolean, imageUrl?: string, error?: string}> => {
    console.log(`[ChatService] Uploading image for user ${userAddress}`);
    const url = `${apiUrl}/api/messages/upload-image`; // Ensure apiUrl is correct
    const formData = new FormData();
    formData.append('image', imageFile);
    // Add walletAddress to the form data if your backend needs it for auth here
    formData.append('walletAddress', userAddress); // <-- Add walletAddress for isAuthenticated middleware

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            // Include credentials/auth tokens if needed
            // headers: { 'Authorization': `Bearer ${your_token}` }
        });

        // --- Check response status BEFORE parsing JSON ---
        if (!response.ok) {
            // Try to get error text, but don't assume JSON
            const errorText = await response.text();
            console.error(`[ChatService] Failed to upload image (${url}): ${response.status}`, errorText);
            // Try parsing as JSON, but fallback to raw text
            let errorMessage = `Failed to upload image. Status: ${response.status}`;
            try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.error || errorMessage;
            } catch (parseError) {
                // If it's not JSON (like HTML error page), use the raw text if short, or a generic message
                errorMessage = errorText.length < 100 ? errorText : errorMessage;
            }
            throw new Error(errorMessage);
        }
        // --- End status check ---

        // If response.ok is true, expect JSON
        const responseData = await response.json();

        if (!responseData.success || !responseData.imageUrl) {
            console.error(`[ChatService] Invalid success response format from ${url}:`, responseData);
            throw new Error("Invalid response from server after uploading image.");
        }

        console.log('[ChatService] Image uploaded successfully:', responseData.imageUrl);
        return { success: true, imageUrl: responseData.imageUrl };

    } catch (error) {
        console.error(`[ChatService] Network or processing error in uploadImage (${url}):`, error);
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
};
// --- END: New Function ---


/**
 * Fetches the message history between two users.
 * @param userAddress - The address of the logged-in user.
 * @param otherUserAddress - The address of the user being chatted with.
 * @returns Promise<Message[]> - A promise resolving to an array of messages.
 */
export const getMessageHistory = async (userAddress: string, otherUserAddress: string): Promise<Message[]> => {
    // ... existing implementation ...
    // Note: The backend now populates replyToMessage if replyToMessageId exists
    console.log(`[ChatService] Fetching history between ${userAddress} and ${otherUserAddress}`);
    const url = `${apiUrl}/api/messages/${otherUserAddress}?walletAddress=${encodeURIComponent(userAddress)}`;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                // Add authentication headers if required
            },
        });

        const responseBody = await response.text(); // Get raw response body for logging

        if (!response.ok) {
            // ... existing error handling ...
            let errorData = {};
            try { errorData = JSON.parse(responseBody); } catch (e) { errorData = { error: responseBody || `HTTP error ${response.status}` }; }
            console.error(`[ChatService] Failed to fetch history (${url}): ${response.status}`, errorData);
            throw new Error((errorData as any).error || `Failed to load messages. Status: ${response.status}`);
        }

        const messages: Message[] = JSON.parse(responseBody);
        console.log(`[ChatService] Received ${messages.length} messages from ${url}. Messages have populated replies.`);
        // Backend now sorts and populates, frontend just returns
        // messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()); // Sorting now done on backend
        return messages;
    } catch (error) {
        // ... existing error handling ...
        console.error(`[ChatService] Network or parsing error in getMessageHistory (${url}):`, error);
        throw new Error(error instanceof Error ? error.message : String(error));
    }
};

/**
 * Sends a message via the API.
 * @param senderAddress - The sender's wallet address.
 * @param receiverAddress - The receiver's wallet address.
 * @param messageType - 'text' or 'image'.
 * @param contentOrUrl - The text content (for text) or the image URL (for image).
 * @param replyToMessageId - Optional ID of the message being replied to.
 * @param caption - Optional caption for image messages.
 * @returns Promise<Message> - A promise resolving to the saved message.
 */
export const sendMessage = async (
    senderAddress: string,
    receiverAddress: string,
    messageType: 'text' | 'image',
    // Rename for clarity based on type
    contentOrUrl: string,
    replyToMessageId?: string | null,
    // Add caption parameter specifically for images
    caption?: string // <-- ADDED caption parameter
): Promise<Message> => {
    const token = localStorage.getItem('token');
    const url = `${apiUrl}/api/messages`; // Use constant
    console.log(`[ChatService] Sending message: Type=${messageType}, From=${senderAddress}, To=${receiverAddress}, ReplyTo=${replyToMessageId}, Caption=${caption}`); // Log caption

    const body: any = {
        senderAddress: senderAddress,
        receiverAddress: receiverAddress,
        messageType: messageType,
        replyToMessageId: replyToMessageId || undefined, // Send undefined if null/empty
    };

    // --- FIX: Assign content/imageUrl/caption correctly ---
    if (messageType === 'text') {
        body.content = contentOrUrl;
    } else if (messageType === 'image') {
        body.imageUrl = contentOrUrl; // contentOrUrl is the imageUrl for images
        if (caption) {
            body.content = caption; // Use the 'content' field for the caption
        }
    }
    // --- END FIX ---

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // Assuming Bearer token auth
            },
            body: JSON.stringify(body)
        });

        const responseData = await response.json();

        if (!response.ok) {
            console.error("[ChatService] Send message failed:", responseData);
            throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
        }

        console.log("[ChatService] Message sent successfully:", responseData.message._id); // Use responseData.message
        // Ensure the returned message structure matches the Message interface
        return responseData.message as Message; // Assuming response is { success: true, message: Message }

    } catch (error) {
        console.error("[ChatService] Error sending message:", error);
        // Rethrow or handle as appropriate
        throw error;
    }
};

/**
 * Fetches the count of unread messages for the logged-in user.
 * @param userAddress - The address of the logged-in user.
 * @param groupBySender - If true, returns a map of senderAddress -> count. Otherwise, returns a total count.
 * @returns Promise<number | Record<string, number>> - A promise resolving to the total count or a map.
 */
export const getUnreadMessageCount = async (userAddress: string, groupBySender: boolean = false): Promise<number | Record<string, number>> => {
    console.log(`[ChatService] Fetching unread count for ${userAddress}, groupBySender: ${groupBySender}`);
    const url = `${apiUrl}/api/messages/unread-count?walletAddress=${encodeURIComponent(userAddress)}&groupBySender=${groupBySender}`;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                // Add authentication headers if required
            },
        });

        const responseBody = await response.text();

        if (!response.ok) {
            let errorData = {};
            try { errorData = JSON.parse(responseBody); } catch (e) { errorData = { error: responseBody || `HTTP error ${response.status}` }; }
            console.error(`[ChatService] Failed to fetch unread count (${url}): ${response.status}`, errorData);
            throw new Error((errorData as any).error || `Failed to get unread count. Status: ${response.status}`);
        }

        const responseData = JSON.parse(responseBody);
        if (!responseData.success) {
            console.error(`[ChatService] Invalid success response format from ${url}:`, responseData);
            throw new Error(responseData.error || "Invalid response from server for unread count.");
        }
        console.log(`[ChatService] Received unread count data for ${userAddress}:`, responseData.data);
        return responseData.data; // Returns number or Record<string, number>
    } catch (error) {
        console.error(`[ChatService] Network or parsing error in getUnreadMessageCount (${url}):`, error);
        throw new Error(error instanceof Error ? error.message : String(error));
    }
};

/**
 * Marks messages from a specific sender as read for the logged-in user.
 * @param receiverAddress - The address of the logged-in user.
 * @param senderAddress - The address of the user whose messages should be marked as read.
 * @returns Promise<{success: boolean, message: string}>
 */
export const markMessagesAsRead = async (receiverAddress: string, senderAddress: string): Promise<{success: boolean, message: string}> => {
    console.log(`[ChatService] Marking messages as read for ${receiverAddress} from ${senderAddress}`);
    const url = `${apiUrl}/api/messages/mark-read`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                walletAddress: receiverAddress, // For authentication on backend
                senderAddress: senderAddress,
            }),
        });

        const responseBody = await response.text();

        if (!response.ok) {
            let errorData = {};
            try { errorData = JSON.parse(responseBody); } catch (e) { errorData = { error: responseBody || `HTTP error ${response.status}` }; }
            console.error(`[ChatService] Failed to mark messages as read (${url}): ${response.status}`, errorData);
            throw new Error((errorData as any).error || `Failed to mark messages as read. Status: ${response.status}`);
        }

        const responseData = JSON.parse(responseBody);
        if (!responseData.success) {
             console.error(`[ChatService] Invalid success response format from ${url}:`, responseData);
             throw new Error(responseData.error || "Invalid response from server marking messages read.");
        }
        console.log(`[ChatService] Mark as read successful for ${receiverAddress} from ${senderAddress}`);
        return responseData;

    } catch (error) {
        console.error(`[ChatService] Network or parsing error in markMessagesAsRead (${url}):`, error);
        throw new Error(error instanceof Error ? error.message : String(error));
    }
};

// --- MODIFIED: Function to fetch existing notifications ---
export const fetchNotifications = async (walletAddress: string): Promise<AppNotification[]> => {
    console.log(`[ChatService] Fetching notifications for ${walletAddress}`);
    const token = localStorage.getItem('authToken'); // Or however you store the token

    // --- FIX: Add walletAddress to query parameters as well ---
    const url = `${apiUrl}/api/chat/notifications/${walletAddress}?walletAddress=${encodeURIComponent(walletAddress)}`;
    // --- END FIX ---

    try {
        const response = await fetch(url, { // Use the updated URL
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`, // Keep the token header
                'Content-Type': 'application/json',
            },
        });

        // ... rest of the function remains the same ...
        console.log(`[ChatService] Fetch notifications response status: ${response.status}`);
        const responseBodyText = await response.text(); // Get raw text first
        console.log(`[ChatService] Fetch notifications raw response body:`, responseBodyText);


        if (!response.ok) {
            // Handle specific errors like 403 Forbidden or 401 Unauthorized
            if (response.status === 403 || response.status === 401) {
                 console.warn(`[ChatService] Unauthorized fetching notifications (${response.status}). Token used: ${token ? 'Yes' : 'No'}. URL: ${url}`); // Log URL
                 // Optionally trigger logout or token refresh here
                 return [];
            }
            // Try to parse error from the raw text
            let errorData = { error: `HTTP error ${response.status}` };
            try {
                errorData = JSON.parse(responseBodyText);
            } catch (e) {
                // Keep default error if parsing fails
            }
            throw new Error(errorData.error || `HTTP error ${response.status}`);
        }

        const result = JSON.parse(responseBodyText);

        if (result.success && Array.isArray(result.notifications)) {
            console.log(`[ChatService] Received ${result.notifications.length} notifications successfully.`);
            console.log(`[ChatService] Notifications data:`, result.notifications);
            return result.notifications;
        } else {
            console.warn("[ChatService] Received unexpected format for notifications:", result);
            return [];
        }
    } catch (error) {
        console.error("[ChatService] Failed to fetch notifications:", error);
        return []; // Return empty array on error
    }
};
// --- END MODIFIED ---

// --- REVISED: Confirm Payment for Private Stream Invite ---
export const confirmPayment = async (requestId: string, paymentTxHash: string): Promise<{ success: boolean; message: string; request?: any }> => {
    // Obtener el token de autenticación (si es necesario)
    const token = localStorage.getItem('authToken'); 
    
    // --- MEJORADO: Obtener walletAddress con más opciones y log detallado ---
    console.log(`[ChatService confirmPayment] Verificando localStorage para walletAddress...`);
    
    // Comprobar todas las posibles claves que podrían contener la dirección
    const walletAddress = localStorage.getItem('walletAddress') || 
                         localStorage.getItem('userAddress') || 
                         localStorage.getItem('currentUserAddress') ||
                         localStorage.getItem('connectedAddress');
    
    console.log(`[ChatService confirmPayment] walletAddress encontrada: ${walletAddress || 'NO ENCONTRADA'}`);
    
    if (!walletAddress) {
        // Si no tenemos dirección, intentamos obtenerla desde window.ethereum si está disponible
        try {
            if (window.ethereum && window.ethereum.selectedAddress) {
                console.log(`[ChatService confirmPayment] Usando address desde window.ethereum: ${window.ethereum.selectedAddress}`);
                const addressFromWeb3 = window.ethereum.selectedAddress;
                
                // Almacenar para futuros usos
                localStorage.setItem('walletAddress', addressFromWeb3);
                
                // Construir y enviar la solicitud
                const response = await axios.post(
                    `${apiUrl}/api/streams/private/request/${requestId}/pay`,
                    { 
                        paymentTxHash,
                        walletAddress: addressFromWeb3
                    },
                    {
                        headers: {
                            'Authorization': token ? `Bearer ${token}` : '',
                            'Content-Type': 'application/json',
                        },
                    }
                );
                console.log("[ChatService] Confirm payment response:", response.data);
                return response.data;
            }
        } catch (web3Error) {
            console.error(`[ChatService] Error obteniendo wallet desde window.ethereum:`, web3Error);
        }
        
        throw new Error("Wallet address required. Please reconnect your wallet.");
    }

    try {
        console.log(`[ChatService] Confirming payment for request ${requestId}, Tx: ${paymentTxHash}, Address: ${walletAddress}`);
        
        // --- MEJORADO: Agregar más parámetros para identificación ---
        const response = await axios.post(
            `${apiUrl}/api/streams/private/request/${requestId}/pay`,
            { 
                paymentTxHash,
                walletAddress,         // Dirección principal
                userAddress: walletAddress, // Alias adicional
                senderAddress: walletAddress, // Otro posible alias que el backend reconozca
                invitedAddress: walletAddress // Por si el backend busca este parámetro específico
            },
            {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                },
            }
        );
        console.log("[ChatService] Confirm payment response:", response.data);
        return response.data;
    } catch (error: any) {
        console.error(`[ChatService] Error confirming payment for request ${requestId}:`, error.response?.data || error.message);
        throw new Error(error.response?.data?.error || error.message || 'Failed to confirm payment.');
    }
};

// --- MODIFIED: Reject Private Stream Invite ---
export const rejectInvite = async (requestId: string): Promise<{ success: boolean; message: string; request?: any }> => {
    // Obtener el token de autenticación (si es necesario)
    const token = localStorage.getItem('authToken');
    
    // --- NUEVO: Obtener automáticamente la dirección de wallet desde localStorage ---
    const walletAddress = localStorage.getItem('walletAddress') || 
                         localStorage.getItem('userAddress') || 
                         localStorage.getItem('currentUserAddress');
    
    if (!walletAddress) {
        throw new Error("Wallet address required. Please reconnect your wallet.");
    }
    // --- FIN NUEVO ---

    try {
        console.log(`[ChatService] Rejecting invite request ${requestId}, Address: ${walletAddress}`);
        const response = await axios.post(
            `${apiUrl}/api/streams/private/request/${requestId}/reject`,
            { 
                walletAddress // Incluir el walletAddress en todos los casos
            },
            {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '', // Incluir token si existe
                    'Content-Type': 'application/json',
                },
            }
        );
        console.log("[ChatService] Reject invite response:", response.data);
        return response.data; // Should return { success: true, message: "...", request: {...} }
    } catch (error: any) {
        console.error(`[ChatService] Error rejecting invite request ${requestId}:`, error.response?.data || error.message);
        throw new Error(error.response?.data?.error || error.message || 'Failed to reject invitation.');
    }
};

/**
 * Deletes specific messages by their IDs.
 * Requires authentication (handled by backend middleware based on request context).
 * @param messageIds - An array of message IDs to delete.
 * @returns Promise resolving to the backend response (e.g., { success: boolean, deletedCount: number }).
 */
export const deleteMessages = async (messageIds: string[]): Promise<{ success: boolean; deletedCount: number; error?: string }> => {
    console.log(`[ChatService] Deleting messages: ${messageIds.join(', ')}`);
    try {
        const response = await fetch(`${apiUrl}/api/chat/messages`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                // Include Authorization header if your auth middleware requires it for DELETE
                // 'Authorization': `Bearer ${your_auth_token}`
            },
            body: JSON.stringify({ messageIds }),
        });

        const result = await response.json();
        console.log(`[ChatService] Delete messages response:`, result);

        if (!response.ok || !result.success) {
            throw new Error(result.error || `Failed to delete messages (HTTP ${response.status})`);
        }

        return result;
    } catch (error: any) {
        console.error('[ChatService Error] deleteMessages:', error);
        return { success: false, deletedCount: 0, error: error.message };
    }
};

/**
 * Clears the entire conversation history between the current user and another user.
 * Requires authentication.
 * @param otherUserAddress - The address of the other user in the conversation.
 * @param senderAddress - Optional: The address of the current user (if not available in localStorage)
 * @returns Promise resolving to the backend response.
 */
export const clearConversation = async (otherUserAddress: string, senderAddress?: string): Promise<{ success: boolean; deletedCount?: number; error?: string }> => {
    console.log(`[ChatService] Clearing conversation with: ${otherUserAddress}`);
    try {
        // Try multiple possible localStorage keys or use provided address
        const currentUserAddress = senderAddress || 
            localStorage.getItem('walletAddress') || 
            localStorage.getItem('userAddress') || 
            localStorage.getItem('currentUserAddress');
        
        if (!currentUserAddress) {
            console.error('[ChatService] No currentUserAddress available for authentication');
            return { success: false, error: 'No user address available. Please reconnect your wallet.' };
        }

        console.log(`[ChatService] Using address for authentication: ${currentUserAddress}`);

        // Usar un timeout más largo para dar tiempo a la operación
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos de timeout

        try {
            const response = await fetch(`${apiUrl}/api/chat/messages/conversation/${otherUserAddress}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                // Include all possible parameter names the backend might be looking for
                body: JSON.stringify({ 
                    userAddress: currentUserAddress,
                    walletAddress: currentUserAddress,
                    senderAddress: currentUserAddress
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            const data = await response.json();
            console.log(`[ChatService] Clear conversation response:`, data);

            if (!response.ok) {
                throw new Error(data.error || 'Failed to clear conversation');
            }

            return { 
                success: true, 
                deletedCount: data.deletedCount || 0 // Asegurar que exista deletedCount 
            };
        } catch (fetchError) {
            if (fetchError.name === 'AbortError') {
                throw new Error('Request timeout: The operation took too long to complete');
            }
            throw fetchError;
        }
    } catch (error) {
        console.error('[ChatService Error] clearConversation:', error);
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error clearing conversation'
        };
    }
};

/**
 * Verifica si un usuario está bloqueado por el usuario actual
 * @param targetAddress - Dirección del usuario a verificar
 * @param currentUserAddress - Dirección del usuario actual (opcional)
 * @returns Promise con el estado de bloqueo
 */
export const checkBlockStatus = async (targetAddress: string, currentUserAddress?: string): Promise<{ success: boolean; isBlocked: boolean; error?: string }> => {
    try {
        // Intentar obtener la dirección del usuario actual
        const userAddress = currentUserAddress || 
            localStorage.getItem('walletAddress') || 
            localStorage.getItem('userAddress') || 
            localStorage.getItem('currentUserAddress');
        
        if (!userAddress) {
            return { success: false, isBlocked: false, error: 'No user address available.' };
        }

        console.log(`[chatService] Checking block status: current=${userAddress}, target=${targetAddress}`);

        // FIX: Use the correct endpoint path
        const response = await fetch(`${apiUrl}/api/chat/block/status/${targetAddress}?walletAddress=${encodeURIComponent(userAddress)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return { success: true, isBlocked: data.isBlocked };
        
    } catch (error) {
        console.error('[ChatService Error] checkBlockStatus:', error);
        return { 
            success: false, 
            isBlocked: false,
            error: error instanceof Error ? error.message : 'Unknown error checking block status'
        };
    }
};

// --- NEW: Block a user ---
/**
 * Blocks another user, preventing further communication.
 * Requires authentication.
 * @param addressToBlock - The address of the user to block.
 * @param senderAddress - Optional: The address of the current user (if not available in localStorage)
 * @returns Promise resolving to the backend response.
 */
export const blockUser = async (addressToBlock: string, senderAddress?: string): Promise<{ success: boolean; error?: string }> => {
    try {
        // Try multiple possible localStorage keys or use provided address
        const currentUserAddress = senderAddress || 
            localStorage.getItem('walletAddress') || 
            localStorage.getItem('userAddress') || 
            localStorage.getItem('currentUserAddress');
        
        if (!currentUserAddress) {
            console.log('[blockUser] No currentUserAddress available for authentication');
            return { success: false, error: 'No user address available. Please reconnect your wallet.' };
        }

        console.log(`[blockUser] Using address for authentication: ${currentUserAddress}`);

        const response = await fetch(`${apiUrl}/api/chat/block/${addressToBlock}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            // Include all possible parameter names the backend might be looking for
            body: JSON.stringify({ 
                userAddress: currentUserAddress,
                walletAddress: currentUserAddress,
                senderAddress: currentUserAddress
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            console.log('[blockUser]', data.error || 'Failed to block user');
            throw new Error(data.error || 'Failed to block user');
        }

        return { success: true };
    } catch (error) {
        console.error('[ChatService Error] blockUser:', error);
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error blocking user'
        };
    }
};

// --- NEW: Unblock a user ---
/**
 * Unblocks another user, allowing communication again.
 * Requires authentication.
 * @param unblockedAddress - The address of the user to unblock.
 * @param senderAddress - Optional: The address of the current user (if not available in localStorage)
 * @returns Promise resolving to the backend response.
 */
export const unblockUser = async (addressToUnblock: string, senderAddress?: string): Promise<{ success: boolean; error?: string }> => {
    try {
        // Try multiple possible localStorage keys or use provided address
        const currentUserAddress = senderAddress || 
            localStorage.getItem('walletAddress') || 
            localStorage.getItem('userAddress') || 
            localStorage.getItem('currentUserAddress');
        
        if (!currentUserAddress) {
            return { success: false, error: 'No user address available. Please reconnect your wallet.' };
        }

        console.log(`[unblockUser] Using address for authentication: ${currentUserAddress}`);

        const response = await fetch(`${apiUrl}/api/chat/block/${addressToUnblock}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            // Include all possible parameter names the backend might be looking for
            body: JSON.stringify({ 
                userAddress: currentUserAddress,
                walletAddress: currentUserAddress,
                senderAddress: currentUserAddress
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to unblock user');
        }

        return { success: true };
    } catch (error) {
        console.error('[ChatService Error] unblockUser:', error);
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error unblocking user'
        };
    }
};

// New interfaces for streams and wallet
export interface PrivateStreamInfo {
  _id: string;
  streamerAddress: string;
  invitedAddress: string;
  entryPriceBNB: number;
  scheduleInfo: string;
  status: 'pending' | 'paid' | 'active' | 'completed' | 'rejected';
  streamerInternalWallet: string;
  createdAt: string;
  updatedAt: string;
  paymentTxHash?: string;
  streamerUsername?: string;
  streamerProfilePicture?: string;
  invitedUsername?: string;
  invitedProfilePicture?: string;
  startTime?: string;
  endTime?: string;
}

export interface InternalWalletInfo {
  address: string;
  balance: string; // BNB balance as string
  transactions?: {
    txHash: string;
    amount: string;
    type: 'deposit' | 'withdrawal';
    timestamp: string;
    status: 'pending' | 'confirmed' | 'failed';
  }[];
}

/**
 * Fetches private stream requests for the user
 * @param walletAddress User's wallet address
 * @param status Optional status filter (pending, paid, active, completed, rejected)
 * @returns Promise with array of private stream requests
 */
export const fetchPrivateStreams = async (
  walletAddress: string,
  status?: 'pending' | 'paid' | 'active' | 'completed' | 'rejected'
): Promise<PrivateStreamInfo[]> => {
  console.log(`[ChatService] Fetching private streams for ${walletAddress}${status ? `, status: ${status}` : ''}`);
  try {
    // Correct endpoint path
    const endpoint = `${apiUrl}/api/streams/private/requests`;
    
    // Important: Need to explicitly ask for both streamer and invited streams using role=both parameter
    // The backend API is only checking for invited streams by default (as seen in logs)
    const response = await axios.get(endpoint, {
      params: { 
        walletAddress,
        status,
        role: 'both',               // Request both roles explicitly
        includeStreamerStreams: true, // This parameter may be needed depending on backend
        includeInvitedStreams: true,  // Explicitly request invited streams too
        streamerAddress: walletAddress, // Also explicitly pass as streamerAddress
        invitedAddress: walletAddress,  // And as invitedAddress to cover all bases
      }
    });
    
    // Log the response for debugging
    console.log(`[ChatService] Fetched streams API response:`, response.data);
    
    if (Array.isArray(response.data)) {
      console.log(`[ChatService] Fetched ${response.data.length} private streams:`, 
        response.data.map(s => ({ 
          id: s._id, 
          streamer: s.streamerAddress?.substring(0, 6), 
          invited: s.invitedAddress?.substring(0, 6),
          status: s.status
        }))
      );
      
      return response.data;
    } else {
      console.warn(`[ChatService] Unexpected response format from ${endpoint}:`, response.data);
      return [];
    }
  } catch (error: any) {
    console.error('[ChatService] Error fetching private streams:', 
      error.response?.status 
        ? `Status ${error.response.status}: ${error.response.statusText}`
        : error.message);
        
    // Also log the response body if available for debugging
    if (error.response?.data) {
      console.error('[ChatService] Error response body:', error.response.data);
    }
    
    throw new Error(error.response?.data?.error || 'Failed to fetch private streams');
  }
};

/**
 * Specifically fetches streams where the user is the invited party
 * @param invitedAddress User's wallet address
 * @param status Optional status filter
 * @returns Promise with array of private stream requests
 */
export const fetchInvitedStreams = async (
  invitedAddress: string,
  status?: 'pending' | 'paid' | 'active' | 'completed' | 'rejected'
): Promise<PrivateStreamInfo[]> => {
  console.log(`[ChatService] Explicitly fetching streams where user is invited: ${invitedAddress}${status ? `, status: ${status}` : ''}`);
  try {
    const endpoint = `${apiUrl}/api/streams/private/requests`;
    
    // Only ask for streams where the user is the invited party using direct query
    const response = await axios.get(endpoint, {
      params: { 
        walletAddress: invitedAddress, // Auth param
        invitedAddress,               // Explicit role param
        status,
        asInvited: true,              // Optional flag
        role: 'invited'               // Role specification
      }
    });
    
    // Log the response
    console.log(`[ChatService] Fetched invited streams response:`, response.data);
    
    if (Array.isArray(response.data)) {
      console.log(`[ChatService] Fetched ${response.data.length} streams where ${invitedAddress} is invited`);
      return response.data;
    }
    
    return [];
  } catch (error: any) {
    console.error('[ChatService] Error fetching invited streams:', error.message);
    return []; // Return empty array rather than throwing to allow fallback chains
  }
};

/**
 * Fetches streams created by the streamer (as a fallback if the main function doesn't work)
 * @param streamerAddress User's wallet address who created the streams
 * @param status Optional status filter
 * @returns Promise with array of private stream requests
 */
export const fetchStreamerCreatedStreams = async (
  streamerAddress: string,
  status?: 'pending' | 'paid' | 'active' | 'completed' | 'rejected'
): Promise<PrivateStreamInfo[]> => {
  console.log(`[ChatService] Fetching streams created by streamer ${streamerAddress}${status ? `, status: ${status}` : ''}`);
  try {
    // Use the same endpoint but with streamer parameter
    const endpoint = `${apiUrl}/api/streams/private/requests`;
    
    // FIX: Include BOTH walletAddress AND streamerAddress, and use more explicit parameters
    const response = await axios.get(endpoint, {
      params: { 
        walletAddress: streamerAddress,    // For authentication
        streamerAddress,                   // For filtering streams created by this user
        role: 'streamer',                  // Explicitly request streamer role
        status,
        asStreamer: true,                  // Extra parameter
        includeStreamerStreams: true,      // Extra parameter
        onlyStreamerStreams: true,         // New parameter to filter only streams created by user
        // Don't include invitedAddress to avoid confusion
      }
    });
    
    // Add backup API call when regular one returns empty results
    if (Array.isArray(response.data) && response.data.length === 0) {
      console.log(`[ChatService] No streams found with primary endpoint, trying direct streamer endpoint`);
      try {
        // Try a more direct endpoint that may be implemented on backend
        const backupResponse = await axios.get(`${apiUrl}/api/streams/private/streamer/${streamerAddress}`, {
          params: { 
            walletAddress: streamerAddress
          }
        });
        
        if (Array.isArray(backupResponse.data) && backupResponse.data.length > 0) {
          console.log(`[ChatService] Backup endpoint returned ${backupResponse.data.length} streams`);
          return backupResponse.data;
        }
      } catch (backupError) {
        console.log(`[ChatService] Backup endpoint failed:`, backupError.message);
        // Continue with original response if backup fails
      }
    }
    
    if (Array.isArray(response.data)) {
      console.log(`[ChatService] Fetched ${response.data.length} streams created by ${streamerAddress}:`, 
        response.data.map(s => ({ 
          id: s._id, 
          streamer: s.streamerAddress?.substring(0, 6), 
          invited: s.invitedAddress?.substring(0, 6),
          status: s.status
        }))
      );
      
      return response.data;
    }
    
    return [];
  } catch (error: any) {
    console.error('[ChatService] Error fetching streamer created streams:', error.message);
    return []; // Return empty array instead of throwing to allow fallback chain
  }
};

/**
 * Fetches internal wallet info including balance
 * @param walletAddress User's wallet address
 * @returns Promise with internal wallet info
 */
export const fetchInternalWalletInfo = async (walletAddress: string): Promise<InternalWalletInfo> => {
  console.log(`[ChatService] Fetching internal wallet balance for ${walletAddress}`);
  try {
    const response = await axios.get(`${apiUrl}/api/profile/internal-wallet/balance`, {
      params: { walletAddress }
    });
    
    console.log('[ChatService] Fetched internal wallet balance:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('[ChatService] Error fetching internal wallet balance:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch internal wallet balance');
  }
};

// Keep getInternalWalletBalance as an alias for backward compatibility
export const getInternalWalletBalance = fetchInternalWalletInfo;

/**
 * Initiates a withdrawal from internal wallet to user's external wallet
 * @param walletAddress User's wallet address
 * @param amount Amount to withdraw in BNB
 * @returns Promise with withdrawal result
 */
export const withdrawFromInternalWallet = async (
  walletAddress: string,
  amount: string
): Promise<{ success: boolean; txHash?: string; message: string }> => {
  console.log(`[ChatService] Initiating withdrawal of ${amount} BNB for ${walletAddress}`);
  try {
    const response = await axios.post(`${apiUrl}/api/profile/internal-wallet/withdraw`, {
      walletAddress,
      amount
    });
    
    console.log('[ChatService] Withdrawal initiated:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('[ChatService] Error initiating withdrawal:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to initiate withdrawal');
  }
};

/**
 * Enhanced check for block status that provides more detail
 */
export const checkDetailedBlockStatus = async (targetAddress: string, currentUserAddress?: string): Promise<{ 
  success: boolean; 
  isBlocked: boolean; 
  amIBlocked: boolean;
  error?: string 
}> => {
  try {
    // Get current user address from params or localStorage
    const userAddress = currentUserAddress || 
        localStorage.getItem('walletAddress') || 
        localStorage.getItem('userAddress') || 
        localStorage.getItem('currentUserAddress');
    
    if (!userAddress) {
        return { 
          success: false, 
          isBlocked: false, 
          amIBlocked: false, 
          error: 'No user address available.'
        };
    }

    console.log(`[ChatService] Checking detailed block status: current=${userAddress}, target=${targetAddress}`);

    // Check if current user has blocked target
    const meBlockingThemResponse = await fetch(`${apiUrl}/api/chat/block/status/${targetAddress}?walletAddress=${encodeURIComponent(userAddress)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    // Check if target has blocked current user
    const themBlockingMeResponse = await fetch(`${apiUrl}/api/chat/block/status/${userAddress}?walletAddress=${encodeURIComponent(targetAddress)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!meBlockingThemResponse.ok || !themBlockingMeResponse.ok) {
        throw new Error(`HTTP error! status: ${meBlockingThemResponse.status}/${themBlockingMeResponse.status}`);
    }
    
    const meBlockingThemData = await meBlockingThemResponse.json();
    const themBlockingMeData = await themBlockingMeResponse.json();

    console.log(`[ChatService] Block status results:`, {
      meBlockingThem: meBlockingThemData.isBlocked,
      themBlockingMe: themBlockingMeData.isBlocked
    });
    
    return { 
      success: true, 
      isBlocked: meBlockingThemData.isBlocked, // I blocked them
      amIBlocked: themBlockingMeData.isBlocked // They blocked me
    };
    
  } catch (error) {
    console.error('[ChatService] Error checking detailed block status:', error);
    return { 
        success: false, 
        isBlocked: false,
        amIBlocked: false,
        error: error instanceof Error ? error.message : 'Unknown error checking block status'
    };
  }
};

/**
 * Inicia un stream privado, cambiando su estado a 'active'
 * @param streamId ID del stream a iniciar
 * @param walletAddress Dirección del usuario que inicia el stream
 * @returns Promise con el resultado de la operación
 */
export const startPrivateStream = async (
  streamId: string, 
  walletAddress: string
): Promise<{ success: boolean; message: string; stream?: PrivateStreamInfo }> => {
  console.log(`[ChatService] Iniciando stream ${streamId} para ${walletAddress}`);
  try {
    const response = await axios.post(`${apiUrl}/api/streams/private/request/${streamId}/start`, {
      walletAddress // Incluir la dirección para autenticación
    });
    
    console.log("[ChatService] Stream inicio response:", response.data);
    return response.data;
  } catch (error: any) {
    console.error(`[ChatService] Error iniciando stream ${streamId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.message || 'Failed to start stream');
  }
};

/**
 * Simplifica la obtención de streams privados realizando todas las consultas necesarias de una vez,
 * priorizando la búsqueda de invitaciones pendientes
 * @param walletAddress Dirección del usuario
 * @returns Promise con array de streams privados
 */
export const fetchAllUserStreams = async (walletAddress: string): Promise<PrivateStreamInfo[]> => {
  console.log(`[ChatService] Fetching all streams for ${walletAddress}`);
  
  // Array para almacenar todos los streams
  let allStreams: PrivateStreamInfo[] = [];
  
  try {
    // PRIORIDAD 1: Try the most direct method - get all requests associated with this user
    console.log(`[ChatService] Trying primary approach: Direct fetch of all streams for ${walletAddress}`);
    try {
      const directResponse = await axios.get(`${apiUrl}/api/streams/private/requests`, {
        params: { 
          walletAddress,
          role: 'both',               // Request both roles explicitly
          includeAll: true            // Request all related streams
        }
      });
      
      if (Array.isArray(directResponse.data) && directResponse.data.length > 0) {
        console.log(`[ChatService] Direct fetch returned ${directResponse.data.length} streams`);
        allStreams = [...directResponse.data];
        return allStreams; // Return immediately if we have success with the direct approach
      }
    } catch (directError) {
      console.error("[ChatService] Direct fetch failed:", directError);
      // Continue with fallback methods if this fails
    }
    
    // FALLBACK 1: Check for streams by status, one by one
    console.log(`[ChatService] Fallback 1: Fetching streams by status`);
    
    // Start with pending streams (highest priority for UI)
    try {
      const pendingResponse = await axios.get(`${apiUrl}/api/streams/private/requests`, {
        params: { 
          walletAddress,
          status: 'pending'
        }
      });
      
      if (Array.isArray(pendingResponse.data) && pendingResponse.data.length > 0) {
        console.log(`[ChatService] Found ${pendingResponse.data.length} pending streams`);
        allStreams = [...allStreams, ...pendingResponse.data];
      }
    } catch (pendingError) {
      console.error("[ChatService] Error fetching pending streams:", pendingError);
    }
    
    // Next try paid streams
    try {
      const paidResponse = await axios.get(`${apiUrl}/api/streams/private/requests`, {
        params: { 
          walletAddress,
          status: 'paid'
        }
      });
      
      if (Array.isArray(paidResponse.data) && paidResponse.data.length > 0) {
        console.log(`[ChatService] Found ${paidResponse.data.length} paid streams`);
        allStreams = [...allStreams, ...paidResponse.data];
      }
    } catch (paidError) {
      console.error("[ChatService] Error fetching paid streams:", paidError);
    }
    
    // Finally try active streams
    try {
      const activeResponse = await axios.get(`${apiUrl}/api/streams/private/requests`, {
        params: { 
          walletAddress,
          status: 'active'
        }
      });
      
      if (Array.isArray(activeResponse.data) && activeResponse.data.length > 0) {
        console.log(`[ChatService] Found ${activeResponse.data.length} active streams`);
        allStreams = [...allStreams, ...activeResponse.data];
      }
    } catch (activeError) {
      console.error("[ChatService] Error fetching active streams:", activeError);
    }
    
    // FALLBACK 2: Check if the user is specifically in the invited field
    if (allStreams.length === 0) {
      console.log(`[ChatService] Fallback 2: Checking if user is invited to any streams`);
      try {
        const invitedParams = {
          walletAddress,
          invitedAddress: walletAddress
        };
        
        // Try with both the /requests and /invites endpoints
        const invitedResponse = await axios.get(`${apiUrl}/api/streams/private/requests`, {
          params: invitedParams
        });
        
        if (Array.isArray(invitedResponse.data) && invitedResponse.data.length > 0) {
          console.log(`[ChatService] Found ${invitedResponse.data.length} streams where user is invited`);
          allStreams = [...allStreams, ...invitedResponse.data];
        }
      } catch (invitedError) {
        console.error("[ChatService] Error checking if user is invited:", invitedError);
      }
    }
    
    // Eliminar duplicados
    const uniqueStreams = allStreams.filter((stream, index, self) => 
      index === self.findIndex(s => s._id === stream._id)
    );
    
    console.log(`[ChatService] Final result: ${uniqueStreams.length} unique streams from all queries`);
    return uniqueStreams;
    
  } catch (error) {
    console.error("[ChatService] Error in fetchAllUserStreams:", error);
    return [];
  }
};

// ... rest of the file ...
